'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowLeft, ChevronRight, Menu, X } from 'lucide-react';

import { cn } from '../../lib/utils';
import type { NavItem } from './glp-sidebar';

/**
 * Marks the mobile shell (top bar, drawer) so the desktop sidebar's
 * outside-click collapse ignores clicks made there.
 */
export const GLP_SIDEBAR_MOBILE_ATTR = 'data-glp-sidebar-mobile';

const mobileAttr = { [GLP_SIDEBAR_MOBILE_ATTR]: '' };

const iconButtonClass =
  'inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-accessible-text-color outline-none transition-colors hover:bg-white/15 focus-visible:ring-2 focus-visible:ring-white';

interface GLPMobileTopBarProps {
  open: boolean;
  onToggle: () => void;
  /** id of the drawer the menu button controls. */
  drawerId: string;
  logo?: React.ReactNode;
  /** Right-hand slot, e.g. a user menu. */
  actions?: React.ReactNode;
  className?: string;
  /** Ref to the menu button, so the drawer can hand focus back to it. */
  menuButtonRef?: React.Ref<HTMLButtonElement>;
}

/**
 * Mobile top bar (below `lg`): menu button, logo, actions. Hidden from `lg`
 * up with CSS, so server and client render the same markup.
 *
 * Follows glp-frontend's `glp-mobile-shell-header`. glp's search toggle is
 * left out: the sales-portal has no global search to put there.
 */
export function GLPMobileTopBar({
  open,
  onToggle,
  drawerId,
  logo,
  actions,
  className,
  menuButtonRef,
}: GLPMobileTopBarProps) {
  return (
    <header
      {...mobileAttr}
      className={cn(
        'sticky top-0 z-40 flex h-14 shrink-0 items-center gap-2 bg-[hsl(var(--color-primary))] px-2 shadow-sm lg:hidden',
        className,
      )}
    >
      <button
        ref={menuButtonRef}
        type='button'
        onClick={onToggle}
        aria-label={`${open ? 'Close' : 'Open'} main navigation`}
        aria-expanded={open}
        aria-controls={drawerId}
        className={iconButtonClass}
      >
        <Menu className='h-6 w-6' aria-hidden='true' />
      </button>
      <div className='flex min-w-0 flex-1 items-center justify-center'>{logo}</div>
      <div className='flex min-w-11 shrink-0 items-center justify-end'>{actions}</div>
    </header>
  );
}

interface GLPMobileNavDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  id: string;
  /**
   * Top-level items. An item with two or more `children` opens a second level
   * (drill-down) instead of navigating; with one or none it is a plain link.
   */
  navItems: NavItem[];
  secondaryNavItems?: NavItem[];
  logo?: React.ReactNode;
  /** Accessible name of the drawer. */
  title?: string;
  /** Rendered under the top-level links, e.g. a profile strip. */
  footer?: React.ReactNode;
  /** Element that gets focus back when the drawer closes (the menu button). */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

/** Whether an item opens its own level in the drawer. */
const opensLevel = (item: NavItem) => (item.children?.length ?? 0) > 1;

/**
 * Off-canvas main navigation (below `lg`), after glp-frontend's
 * `MobileNavigationSurface`. A Radix Dialog: focus is trapped and returned to
 * the menu button, Escape and the overlay close it, the page behind does not
 * scroll, and it is announced as a modal dialog.
 *
 * Drill-down, as in glp: a module with section links opens a second level
 * with a back control; the current module's level is open when the drawer
 * opens; modules with a single link navigate directly.
 */
export function GLPMobileNavDrawer({
  open,
  onOpenChange,
  id,
  navItems,
  secondaryNavItems = [],
  logo,
  title = 'Main navigation',
  footer,
  returnFocusRef,
}: GLPMobileNavDrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          {...mobileAttr}
          className='fixed inset-0 z-50 bg-slate-950/40 lg:hidden'
        />
        <DialogPrimitive.Content
          {...mobileAttr}
          id={id}
          // Radix hides the rest of the page from assistive tech but does not
          // set aria-modal itself.
          aria-modal='true'
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            // The drawer is opened from a plain button, not a Dialog.Trigger,
            // so tell Radix where focus goes back to.
            const target = returnFocusRef?.current;
            if (target) {
              event.preventDefault();
              target.focus();
            }
          }}
          className='fixed inset-y-0 left-0 z-50 flex h-dvh w-[min(18rem,84vw)] flex-col overflow-hidden bg-[hsl(var(--color-primary))] shadow-2xl outline-none lg:hidden'
        >
          <DialogPrimitive.Title className='sr-only'>{title}</DialogPrimitive.Title>
          {/* Mounted only while open, so each opening starts from the current module. */}
          <GLPMobileNavLevels
            navItems={navItems}
            secondaryNavItems={secondaryNavItems}
            logo={logo}
            title={title}
            footer={footer}
            onNavigate={() => onOpenChange(false)}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface GLPMobileNavLevelsProps {
  navItems: NavItem[];
  secondaryNavItems: NavItem[];
  logo?: React.ReactNode;
  title: string;
  footer?: React.ReactNode;
  onNavigate: () => void;
}

function GLPMobileNavLevels({
  navItems,
  secondaryNavItems,
  logo,
  title,
  footer,
  onNavigate,
}: GLPMobileNavLevelsProps) {
  // The open level is the label of the module being shown; null is the top level.
  const [levelLabel, setLevelLabel] = React.useState<string | null>(
    () => navItems.find((item) => item.active && opensLevel(item))?.label ?? null,
  );
  const level = levelLabel
    ? navItems.find((item) => item.label === levelLabel && opensLevel(item))
    : undefined;

  const navRef = React.useRef<HTMLElement>(null);
  // What to focus after a level change: the first link of a new level, or the
  // module we came back from.
  const pendingFocus = React.useRef<{ first: true } | { label: string } | null>(null);

  React.useEffect(() => {
    const target = pendingFocus.current;
    const nav = navRef.current;
    if (!target || !nav) return;
    pendingFocus.current = null;
    const el =
      'first' in target
        ? nav.querySelector<HTMLElement>('a, button')
        : Array.from(nav.querySelectorAll<HTMLElement>('[data-nav-label]')).find(
            (node) => node.dataset.navLabel === target.label,
          );
    el?.focus();
  }, [levelLabel]);

  const enter = (item: NavItem) => {
    pendingFocus.current = { first: true };
    setLevelLabel(item.label);
  };
  const back = () => {
    pendingFocus.current = level ? { label: level.label } : null;
    setLevelLabel(null);
  };

  return (
    <>
      <div className='flex min-h-14 shrink-0 items-center gap-2 border-b border-white/25 py-2 pl-2 pr-2'>
        {level ? (
          <>
            <button
              type='button'
              onClick={back}
              className={iconButtonClass}
              aria-label={`Back to ${title.toLowerCase()}`}
            >
              <ArrowLeft className='h-5 w-5' aria-hidden='true' />
            </button>
            <div className='min-w-0 flex-1'>
              <p className='truncate text-[0.6875rem] font-semibold uppercase tracking-wide text-accessible-text-color/75'>
                {title}
              </p>
              <h2 className='truncate text-lg font-bold leading-tight text-accessible-text-color'>
                {level.label}
              </h2>
            </div>
          </>
        ) : (
          <div className='min-w-0 flex-1 pl-2'>{logo}</div>
        )}
        <DialogPrimitive.Close
          className={iconButtonClass}
          aria-label={`Close ${title.toLowerCase()}`}
        >
          <X className='h-5 w-5' aria-hidden='true' />
        </DialogPrimitive.Close>
      </div>

      <nav
        ref={navRef}
        aria-label={level ? `${level.label} navigation` : title}
        className='min-h-0 flex-1 overflow-y-auto py-3 pl-3'
      >
        <ul className='space-y-1'>
          {(level ? level.children! : navItems).map((item) => (
            <li key={item.label}>
              {!level && opensLevel(item) ? (
                <button
                  type='button'
                  data-nav-label={item.label}
                  onClick={() => enter(item)}
                  className={navRowClass(item.active)}
                >
                  <item.icon className='h-5 w-5 shrink-0' />
                  <span className='min-w-0 flex-1 truncate text-left'>{item.label}</span>
                  <ChevronRight className='h-4 w-4 shrink-0' aria-hidden='true' />
                </button>
              ) : (
                <GLPMobileNavLink item={item} onNavigate={onNavigate} />
              )}
            </li>
          ))}
        </ul>
        {!level && secondaryNavItems.length > 0 && (
          <ul className='mt-4 space-y-1 border-t border-white/60 pt-4'>
            {secondaryNavItems.map((item) => (
              <li key={item.label}>
                <GLPMobileNavLink item={item} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        )}
      </nav>

      {!level && footer && (
        <div className='shrink-0 border-t border-accessible-text-color/20 p-3'>{footer}</div>
      )}
    </>
  );
}

const navRowClass = (active?: boolean) =>
  cn(
    'flex min-h-11 w-full items-center gap-3 rounded-l-[19px] px-3 py-2 text-sm font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-white',
    active
      ? 'bg-white text-[hsl(var(--color-primary))]'
      : 'text-accessible-text-color hover:bg-white/90 hover:text-[hsl(var(--color-primary))]',
  );

function GLPMobileNavLink({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const Icon = item.icon;

  const handleClick = (e: React.MouseEvent) => {
    // Let modified clicks (new tab / window) through to the browser.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (item.onClick) {
      e.preventDefault();
      item.onClick();
    }
    onNavigate();
  };

  return (
    <a
      href={item.href ?? '#'}
      onClick={handleClick}
      aria-current={item.active ? 'page' : undefined}
      className={navRowClass(item.active)}
    >
      <Icon className='h-5 w-5 shrink-0' />
      <span className='min-w-0 flex-1 truncate'>{item.label}</span>
    </a>
  );
}
